import { call, put, select } from 'redux-saga/effects';
import moment from 'moment';
import { toast } from 'react-toastify';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import pickBy from 'ramda/src/pickBy';
import queryString from 'query-string';
import { AppActions, CommunityActions, CompanyActions, DevelopActions, ManageActions, VendorActions } from '~/actions';
import { history } from '~/reducers';
import { isNotNull } from '~/services/util';
import decamelize from '~/services/decamelize';
import { COM_BLOG_CATEGORIES_STORAGE_KEY } from '~/services/constants';
import NavigationService from '~/services/NavigationService';

import xmljs from 'xml-js';

export function* loginRequest(api, action) {
    try {
        const { payload, route, after } = action;
        const response = yield api.loginByEmail(payload);

        if (response.ok) {
            const { token, user } = response.data;
            const dateStart = moment().startOf('month').format('YYYY-MM-DD');
            const dateEnd = moment().endOf('month').format('YYYY-MM-DD');
            const isCommunityUser = user.company_id === 38;
            localStorage.setItem('token', token);

            yield put(AppActions.loginSuccess({ token, user }));
            yield put(AppActions.getuserhealthplanRequest(user.id));
            yield put(AppActions.postmulticompanydataRequest([{ id: user.company_id }]));
            yield put(AppActions.postmulticompanydataRequest());
            yield put(AppActions.getauthoringRequest());
            if (isCommunityUser) yield put(AppActions.getemployeesearchRequest({}));
            yield put(AppActions.getcompanyRequest(user.company_info.id));
            yield put(AppActions.fetchnotificationsRequest());
            yield put(AppActions.getcommunityusersRequest(user.community_user_id));
            yield put(AppActions.getlibraryfiltersRequest());
            yield put(AppActions.getusercompaniesRequest(user.id));
            yield put(ManageActions.selectusersRequest([user.id]));
            yield put(ManageActions.companyindividualRequest({ companyId: [user.company_id], userId: null, dateStart, dateEnd }, false));
            yield put(DevelopActions.careerdevreportsRequest(user.company_info.id, user.id));
            yield put(
                VendorActions.getcategoriesRequest({
                    isCategory: window.location.pathname !== '/vendor-ratings/by-category',
                })
            );
            if (after) yield put({ ...after });
            if (route) {
                if (route === '/community/profile') {
                    yield call(NavigationService.push, `/${user.community_user?.entity?.slug}/about`);
                } else {
                    yield call(NavigationService.push, route);
                }
            } else {
                isCommunityUser ? yield call(NavigationService.push, '/community') : yield call(NavigationService.push, '/library/training');
            }
        } else {
            toast.error('Oops, username or password is wrong!', { position: toast.POSITION.TOP_RIGHT });
            yield put(AppActions.loginFailure());
        }
    } catch (e) {
        yield put(AppActions.loginFailure());
    }
}

export function* logoutRequest(api, action) {
    // Clear localStorage token to log out from backend
    localStorage.removeItem('token');
    localStorage.removeItem(COM_BLOG_CATEGORIES_STORAGE_KEY);
    yield put(AppActions.clearRequest());
    yield put(DevelopActions.clearRequest());
    yield put(ManageActions.clearRequest());
    yield put(VendorActions.clearRequest());
    yield put(CommunityActions.clearRequest());
    yield put(CompanyActions.clearRequest());
    yield put(AppActions.logoutSuccess());
}

export function* refreshRequest(api, action) {
    yield put(AppActions.resetRequest());
    yield put(DevelopActions.resetRequest());
    yield put(ManageActions.resetRequest());
    yield put(VendorActions.resetRequest());
    yield put(CommunityActions.resetRequest());
    yield put(CompanyActions.resetRequest());
    yield put(AppActions.refreshSuccess());
}

export function* getslugRequest(api, action) {
    try {
        const { slug } = action;
        const response = yield api.getCompanySlug(slug);
        if (response.ok) {
            yield put(AppActions.getslugSuccess());
        } else {
            yield put(AppActions.getslugFailure());
        }
    } catch (e) {
        yield put(AppActions.getslugFailure());
    }
}

export function* emailverificationRequest(api, action) {
    const { payload, callback } = action;
    const options = queryString.stringify(payload, { arrayFormat: 'bracket' });
    const response = yield api.getEmployeeSearch(options);

    if (response.ok) {
        yield put(AppActions.emailverificationSuccess());
        if (callback) yield callback(response.data);
    } else {
        yield put(AppActions.emailverificationFailure());
    }
}

export function* getauthoringRequest(api) {
    const response = yield api.getAuthoring();
    if (response.ok) {
        yield put(AppActions.getauthoringSuccess(response.data));
    } else {
        yield put(AppActions.getauthoringFailure());
    }
}

export function* getentityconnectionsRequest(api) {
    try {
        const communityUser = yield select((state) => state.app.user.community_user);
        const response = yield api.getEntityConnections(`user_id=${communityUser.id}`);
        if (response.ok) {
            yield put(AppActions.getentityconnectionsSuccess(response.data));
        } else {
            yield put(AppActions.getentityconnectionsFailure());
        }
    } catch (e) {
        yield put(AppActions.getentityconnectionsFailure());
    }
}

export function* getusercompaniesRequest(api, action) {
    try {
        const { userId } = action;
        const response = yield api.getUserCompanies(userId);

        if (response.ok) {
            yield put(AppActions.getusercompaniesSuccess(response.data));
        } else {
            yield put(AppActions.getusercompaniesFailure());
        }
    } catch (e) {
        yield put(AppActions.getusercompaniesFailure());
    }
}

export function* getcompanyvendorRequest(api, action) {
    try {
        const { vendorId } = action;
        const response = yield api.getCompanyVendor(vendorId);

        if (response.ok) {
            yield put(AppActions.getcompanyvendorSuccess(response.data));
        } else {
            yield put(AppActions.getcompanyvendorFailure());
        }
    } catch (e) {
        yield put(AppActions.getcompanyvendorFailure());
    }
}

export function* getlibraryfiltersRequest(api) {
    const response = yield api.getLibraryFilters();
    if (response.ok) {
        yield put(AppActions.getlibraryfiltersSuccess(response.data));
    } else {
        yield put(AppActions.getlibraryfiltersFailure());
    }
}

export function* uploadRequest(api, action) {
    try {
        const { payload, callback, noToast } = action;
        const res = yield api.getS3Info();
        if (res.ok) {
            const formData = new FormData();
            for (const key in res.data.inputs) {
                formData.append(key, res.data.inputs[key]);
            }
            formData.set('Content-Type', payload.file.type);
            formData.append('key', `attachments/${payload.attachment_hash || 'files'}/${payload.file.name}`);
            formData.append('file', payload.file);
            const response = yield api.fileUpload(formData);

            if (response.ok) {
                const data = xmljs.xml2json(response.data, { compact: true, ignoreDeclaration: true });
                const url = {
                    location: JSON.parse(data).PostResponse.Location._text,
                    name: JSON.parse(data).PostResponse.Key._text,
                };
                yield put(AppActions.uploadSuccess(url));
                if (callback) {
                    if (!noToast) toast.info(`file uploaded successfully`, { position: toast.POSITION.TOP_RIGHT });
                    yield callback(url.location);
                }
            } else {
                yield put(AppActions.uploadFailure());
            }
        } else {
            yield put(AppActions.uploadFailure());
        }
    } catch (e) {
        yield put(AppActions.uploadFailure());
    }
}

export function* getcompaniesRequest(api, action) {
    const response = yield api.getCompanies();

    if (response.ok) {
        yield put(AppActions.getcompaniesSuccess(response.data));
    } else {
        yield put(AppActions.getcompaniesFailure());
    }
}

export function* getcompanyRequest(api, action) {
    try {
        const { companyId } = action;
        const response = yield api.getCompany(companyId);

        if (response.ok) {
            yield put(AppActions.getcompanySuccess(response.data));
        } else {
            yield put(AppActions.getcompanyFailure());
        }
    } catch (e) {
        yield put(AppActions.getcompanyFailure());
    }
}

export function* posteditprofileRequest(api, action) {
    try {
        const { userId, payload } = action;
        const response = yield api.postEditProfile(userId, payload);

        if (response.ok) {
            yield put(AppActions.posteditprofileSuccess(response.data));
            yield put(AppActions.getcommunityusersRequest(response.data?.community_user_id));
        } else {
            yield put(AppActions.posteditprofileFailure());
        }
    } catch (e) {
        yield put(AppActions.posteditprofileFailure());
    }
}

export function* postcompanyusersRequest(api, action) {
    try {
        const { companyId, payload, callback, after } = action;
        const options = decamelize(payload);
        const response = yield api.postCompanyUsers(options, companyId);

        if (response.ok) {
            if (after) yield put({ ...after });
            toast.success(payload.user ? 'Successfully updated' : 'Successfully added', {
                position: toast.POSITION.TOP_RIGHT,
            });
            if (callback) yield callback(response);
            yield put(AppActions.postcompanyusersSuccess(response.data.users));
            yield put(AppActions.postmulticompanydataRequest());
            yield put(CompanyActions.getminimalcompanyemployeesRequest(companyId, 1));
            yield put(
                AppActions.getemployeesearchRequest({
                    companyId: [companyId],
                    cacheBust: true,
                })
            );
        } else {
            toast.error('Oops, failed to add a user!', {
                position: toast.POSITION.TOP_RIGHT,
            });
            yield put(AppActions.postcompanyusersFailure());
        }
    } catch (e) {
        yield put(AppActions.postcompanyusersFailure());
    }
}

export function* fetchnotificationsRequest(api, action) {
    try {
        const { page, sort, status, type } = action.payload;

        const options = page ? `?page=${page}` : sort ? `?sort=${sort}` : status ? `?status=${status}` : type ? `?type=${type}` : '';

        const response = yield api.getNotifications(options);

        if (response.ok) {
            yield put(AppActions.fetchnotificationsSuccess(response.data));
        } else {
            yield put(AppActions.fetchnotificationsFailure());
        }
    } catch (e) {
        yield put(AppActions.fetchnotificationsFailure());
    }
}

export function* updatenotificationsRequest(api, action) {
    try {
        const { id, event, after, route } = action;
        const response = yield api.updateNotifications(id, event);

        if (response.ok) {
            yield put(AppActions.fetchnotificationsRequest());
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            yield put(AppActions.updatenotificationsSuccess());
        } else {
            yield put(AppActions.updatenotificationsFailure());
        }
    } catch (e) {
        yield put(AppActions.updatenotificationsFailure());
    }
}

export function* postrecoverypasswordRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postRecoveryPassword(payload);

        if (response.ok) {
            toast.success('Email sent successfully', {
                position: toast.POSITION.TOP_RIGHT,
            });
            yield put(AppActions.postrecoverypasswordSuccess(response.data));
        } else {
            toast.error('Oops, failed to Sent the Email!', {
                position: toast.POSITION.TOP_RIGHT,
            });
            yield put(AppActions.postrecoverypasswordFailure());
        }
    } catch (e) {
        yield put(AppActions.postrecoverypasswordFailure());
    }
}

export function* postmulticompanydataRequest(api, action) {
    try {
        const appCompanies = yield select((state) => state.app.companies);
        const companies = action.payload || appCompanies;
        const companyIds = companies.map((item) => item.id);
        const response = yield api.postMultiCompanyData({ companyId: companyIds });

        if (response.ok) {
            yield put(AppActions.postmulticompanydataSuccess(response.data.companies));
        } else {
            yield put(AppActions.postmulticompanydataFailure());
        }
    } catch (e) {
        yield put(AppActions.postmulticompanydataFailure());
    }
}

export function* postmulticompanyemployeesRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postMultiCompanyEmployees(payload);

        if (response.ok) {
            yield put(AppActions.postmulticompanyemployeesSuccess(response.data.employees));
        } else {
            yield put(AppActions.postmulticompanyemployeesFailure());
        }
    } catch (e) {
        yield put(AppActions.postmulticompanyemployeesFailure());
    }
}

export function* getallauthorsRequest(api, action) {
    try {
        const response = yield api.getAllAuthors();

        if (response.ok) {
            const companyAuthors = filter((e) => e.type === 1, response.data.authors);
            const groupAuthors = filter((e) => e.type === 2, response.data.authors);
            const globalAuthors = filter((e) => e.type === 3, response.data.authors);
            yield put(AppActions.getallauthorsSuccess({ globalAuthors, groupAuthors, companyAuthors }));
        } else {
            yield put(AppActions.getallauthorsFailure());
        }
    } catch (e) {
        yield put(AppActions.getallauthorsFailure());
    }
}

export function* postsaveauthorRequest(api, action) {
    try {
        const { payload, callback } = action;
        const response = yield api.postSaveAuthor(payload);

        if (response.ok) {
            if (callback) yield put(CompanyActions.getbusinessRequest(callback));
            yield put(AppActions.postsaveauthorSuccess(response.data));
        } else {
            yield put(AppActions.postsaveauthorFailure());
        }
    } catch (e) {
        yield put(AppActions.postsaveauthorFailure());
    }
}

export function* postsavebusinessauthorRequest(api, action) {
    try {
        const { payload, callback } = action;
        const options = decamelize(payload);
        const response = yield api.postSaveBusinessAuthor(options);

        if (response.ok) {
            if (callback) yield put(CompanyActions.getbusinessRequest(callback));
            yield put(AppActions.postsavebusinessauthorSuccess(response.data));
        } else {
            yield put(AppActions.postsavebusinessauthorFailure());
        }
    } catch (e) {
        yield put(AppActions.postsavebusinessauthorFailure());
    }
}

export function* postdeleteauthorRequest(api, action) {
    try {
        const { payload, callback } = action;
        const response = yield api.postDeleteAuthor(payload);

        if (response.ok) {
            if (callback) yield put(CompanyActions.getbusinessRequest(callback));
            yield put(AppActions.postdeleteauthorSuccess(response.data));
        } else {
            yield put(AppActions.postdeleteauthorFailure());
        }
    } catch (e) {
        yield put(AppActions.postdeleteauthorFailure());
    }
}

export function* getcommunityusersRequest(api, action) {
    const { userId } = action;
    const response = yield api.getCommunityUser(userId);
    if (response.ok) {
        yield put(AppActions.getcommunityusersSuccess(response.data));
    } else {
        yield put(AppActions.getcommunityusersFailure());
    }
}

export function* getemployeesearchRequest(api, action) {
    const { payload } = action;
    const options = queryString.stringify(decamelize(payload), { arrayFormat: 'bracket' });
    const response = yield api.getEmployeeSearch(options);

    if (response.ok) {
        const { users, company_id } = response.data;
        const userId = users.map((e) => e.id);
        const companyId = company_id;
        const reviewOptions = queryString.stringify(pickBy(isNotNull, decamelize({ userId, companyId, perPage: users.length })), { arrayFormat: 'bracket' });
        const resReviews = yield api.getCompanyPerformanceReviews(reviewOptions);
        const userData = users.map((user) => {
            const reviews = resReviews.data?.users || [];
            const userReview = find((e) => e.user?.id === user.id)(reviews);
            const name = `${user.profile.first_name} ${user.profile.last_name}`;
            return { ...user, name, extra: userReview };
        });
        yield put(AppActions.getemployeesearchSuccess({ users: userData }));
    } else {
        yield put(AppActions.getemployeesearchFailure());
    }
}

export function* getuserjobdescriptionRequest(api, action) {
    const response = yield api.getUserJobDescription();
    if (response.ok) {
        yield put(AppActions.getuserjobdescriptionSuccess(response.data));
    } else {
        yield put(AppActions.getuserjobdescriptionFailure());
    }
}

export function* getuserhealthplanRequest(api, action) {
    try {
        const { userId } = action;
        const response = yield api.getUserPrograms(userId, 'type[]=1&status[]=1&status[]=2');

        if (response.ok && response.data?.data[0]) {
            yield put(AppActions.getuserhealthplanSuccess(response.data?.data[0]));
        } else {
            yield put(AppActions.getuserhealthplanFailure());
        }
    } catch (error) {
        console.error('Error fetching user health plan:', error);
        yield put(AppActions.getuserhealthplanFailure());
    }
}

export function* advancedsearchRequest(api, action) {
    const { tags, target, published, tracking } = action;
    const params = target === 'quotas' ? { quotaTracking: [tracking] } : target === 'scorecards' ? { type: [tracking] } : null;

    yield put(
        DevelopActions.getlibrarytemplatessearchRequest(target, {
            authorId: tags.authors,
            categoryId: tags.categories,
            competencyId: tags.competencies,
            departmentId: tags.departments,
            page: 1,
            perPage: 25,
            published: published ? [1] : [0],
            ...params,
        })
    );
}
